import React from "react";
import { Button, Card, CardContent, CardHeader } from "../../shared/components";
import { useAppSelector, useThunkDispatch } from "../../store";
import { clearDiagnostics, clearDiagnosticsForFile } from "../../store/diagnostics/actions";
import { Diagnostic, DiagnosticBucket } from "../../store/diagnostics/types";
import { Markdown } from "../../shared/components/Markdown";

function formatLocation(d: Diagnostic): string {
    const { line, column } = d.location;
    if (!line && !column) return "";
    const location = column != null ? `${line}:${column}` : `${line}`;
    return d.loopIndex ? `${location} (item #${d.loopIndex})` : location;
}

function renderDiagnostic(d: Diagnostic) {
    return (
        <tr key={d.id}>
            <td><span className={`diagnostic-badge ${d.severity}`}>{d.severity}</span></td>
            <td>{d.origin}</td>
            <td>{d.type}</td>
            <td>{formatLocation(d)}</td>
            <td>
                <Markdown content={d.message} />
            </td>
        </tr>
    );
}

function renderBucket(key: string, bucket: DiagnosticBucket, clearAction: () => void) {
    return (
        <Card key={key} className="diagnostic-group">
            <CardHeader
                title={bucket.fileName ?? "(inline)"}
                action={<Button size="small" variant="contained" color="secondary" onClick={clearAction}>Clear</Button>}
            />
            <CardContent className="flush overflow-hidden">
                <table className="table bordered striped">
                    <thead>
                        <tr>
                            <th>Severity</th>
                            <th>Origin</th>
                            <th>Type</th>
                            <th>Location</th>
                            <th style={{ width: 1024 }}>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bucket.diagnostics.map(renderDiagnostic)}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}

function Diagnostics() {
    const byTemplate = useAppSelector(s => s.diagnostics.byTemplate);
    const dispatch = useThunkDispatch();

    // Only show templates which currently have outstanding diagnostics...
    const entries = Object.entries(byTemplate).filter(([, bucket]) => bucket.diagnostics.length > 0);

    return (
        <div className="diagnostics-layout">
            <div className="diagnostics-toolbar">
                <span className="label">{`${entries.length} template(s) with problems`}</span>
                <Button size="small" variant="contained" color="secondary" disabled={entries.length === 0} onClick={() => dispatch(clearDiagnostics())}>Clear All</Button>
            </div>

            {entries.map(([key, bucket]) => renderBucket(key, bucket, () => dispatch(clearDiagnosticsForFile(key))))}
        </div>
    );
}

export default React.memo(Diagnostics);
